// ReusableFormExceptions.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { translateStatesSendsFalabbelaSelector } from "../../utils/objects/translateStates";
import {
  SendExeptionsIntegrations,
  TempRegisterGeorefcExeptions,
} from "../../redux/accionesOrders";
import { useDispatch } from "react-redux";
import { registrarCambioEnOrden } from "../../redux/accionsRegister";

const normalizeId = (id) => (String(id).startsWith("F") ? id : `F${id}`);

const ReusableFormExceptions = ({
  compyOrder, // { prefix?: string, nonDelivery?: Array }
  open,
  handleClose,
  orderID,
  riderDestails, // { lat/lng ... } opcional
  onSave,
  direct = false, // si true, intentar usar directSelectReason
  directSelectReason = [], // [{ label, value }]
  user = {},
}) => {
  const dispatch = useDispatch();

  const [data, setFormData] = useState({
    id: normalizeId(orderID),
    status: "",
  });

  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);

  // ¿Es Falabella?
  const isFalabella = compyOrder?.prefix === "F";

  // Opciones candidatas en modo "label/value" (Falabella)
  const falabellaOptions = useMemo(() => {
    // Si viene direct y con opciones → prioridad 1
    if (
      direct &&
      Array.isArray(directSelectReason) &&
      directSelectReason.length > 0
    ) {
      return directSelectReason;
    }
    // Si NO hay direct ó vino vacío y es Falabella → usar catálogo conocido
    if (isFalabella && Array.isArray(translateStatesSendsFalabbelaSelector)) {
      return translateStatesSendsFalabbelaSelector;
    }
    return [];
  }, [direct, directSelectReason, isFalabella]);

  // Opciones para otras compañías (name/reasonCode)
  const otherOptions = useMemo(() => {
    return Array.isArray(compyOrder?.nonDelivery) ? compyOrder.nonDelivery : [];
  }, [compyOrder?.nonDelivery]);

  // ¿Qué modelo usar finalmente?
  // - Usamos modelo Falabella si:
  //   a) estamos en direct con opciones, o
  //   b) somos Falabella (prefix === 'F') y tenemos catálogo
  const usingFalabellaModel =
    falabellaOptions.length > 0
      ? true
      : isFalabella && translateStatesSendsFalabbelaSelector?.length > 0;

  // Opciones finales a renderizar
  const options = usingFalabellaModel ? falabellaOptions : otherOptions;

  const noOptions = !options || options.length === 0;

  // Autoselección si hay una sola opción
  useEffect(() => {
    if (noOptions) return;
    if (!data.status && options.length === 1) {
      const only = options[0];
      const nextValue = usingFalabellaModel ? only.value : only.reasonCode;
      setFormData((prev) => ({
        ...prev,
        id: normalizeId(orderID),
        status: nextValue,
      }));
    }
  }, [noOptions, options, usingFalabellaModel, orderID, data.status]);

  const handleChange = (value) => {
    setFormData((prevState) => ({
      ...prevState,
      id: normalizeId(orderID),
      status: value,
    }));
  };

  const handleSave = async (payload) => {
    // evita doble click
    if (saving) return;

    const _id = normalizeId(payload.id);
    if (!_id || !payload.status) {
      console.error("Error: La excepción no tiene un ID o estado válido.");
      return;
    }

    setSaving(true);
    setSent(false);

    // log interno para tu historial propio
    const logData = {
      date: new Date(),
      orderId: orderID || "",
      type: "sendStatus",
      userId: user.uid || "",
      name: user.displayName || "",
      email: user.email || "",
      status: payload.status,
      exceptionID: payload.status,
    };
    console.log("Log excepción:", logData);
    dispatch(registrarCambioEnOrden(logData));

    try {
      // Falabella / direct (label/value)
      if (usingFalabellaModel) {
        const requets = {
          data: { id: _id, status: payload.status },
          riderDestailsLocation: riderDestails || null,
        };

        dispatch(TempRegisterGeorefcExeptions(requets));

        const parentPayload = {
          id: _id,
          status: payload.status,
          exceptionID: payload.status,
          data: { orderID: _id, exceptionID: payload.status },
        };

        // notificas al padre (para que actualice xlogs / isSending si quiere)
        onSave?.({ ...parentPayload, phase: "success" });

        setSent(true);

        // dejamos que el usuario vea "Enviado" un ratito
        setTimeout(() => {
          setSaving(false);
          handleClose(); // 👈 ahora el cierre lo controla este componente
        }, 1200);

        return;
      }

      // OTRAS compañías (usa nonDelivery encontrado)
      const foundObject = otherOptions.find(
        (item) => item.reasonCode === payload.status
      );
      if (!foundObject) {
        console.error("Error: No se encontró el motivo en nonDelivery...");
        setSaving(false);
        return;
      }

      const requets = {
        id: _id,
        status: foundObject.intState,
        date: new Date(),
        reason: foundObject.name,
        reasonCode: foundObject.reasonCode,
      };

      // aquí sí esperamos al thunk
      await dispatch(SendExeptionsIntegrations(requets));

      const parentPayload = {
        id: _id,
        status: foundObject.intState,
        reasonCode: foundObject.reasonCode,
        reason: foundObject.name,
      };

      onSave?.({ ...parentPayload, phase: "success" });

      setSent(true);
      setTimeout(() => {
        setSaving(false);
        handleClose();
      }, 1200);
    } catch (e) {
      console.error("Error enviando excepción:", e);
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      PaperProps={{ style: { width: 500, height: 400 } }}
    >
      <DialogTitle sx={{ color: "#030303" }} id="form-dialog-title">
        Enviar Excepción (Motivo de no entrega)
      </DialogTitle>

      <DialogContent>
        <FormControl fullWidth margin="dense" disabled={noOptions}>
          <InputLabel id="enviarExceptionLabel">
            Seleccione Excepción
          </InputLabel>

          <Select
            labelId="enviarExceptionLabel"
            id="enviarExceptionSelect"
            value={data.status || ""}
            label="Seleccione Excepción"
            onChange={(e) => handleChange(e.target.value)}
          >
            {usingFalabellaModel
              ? options.map((opt, idx) => (
                  <MenuItem key={idx} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))
              : options.map((opt, idx) => (
                  <MenuItem key={idx} value={opt.reasonCode}>
                    {opt.name}
                  </MenuItem>
                ))}
          </Select>

          {noOptions && (
            <FormHelperText>
              No hay motivos disponibles para seleccionar.
            </FormHelperText>
          )}
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={() => handleSave(data)}
          color="primary"
          disabled={!data.status || noOptions || saving} // 👈 aquí
        >
          {saving ? (sent ? "Enviado" : "Enviando…") : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReusableFormExceptions;
